import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from "../common/components/Layout";
import EmployeeDetails from "../common/components/EmployeeDetails.js";
import EmployeeAddress from "../common/components/EmployeeAddress.js";
import EmployeePersonalDetails from "../common/components/EmployeePersonalDetails.js";
import EmployeeHistory from "../common/components/EmploymentHistory.js";
import GovernmentDocs from "../common/components/EmployeeGovernmentDoc.js";
import EmployeeEducationBackground from "../common/components/EducationBackground.js";
import EmployeeEmergencyContact from "../common/components/EmergencyContact.js";

/* ===================== COLLAPSIBLE SECTION COMPONENT ===================== */
const CollapsibleSection = ({ title, icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white rounded-3 shadow-sm border mb-3 overflow-hidden">
      <div 
        className="d-flex justify-content-between align-items-center px-3 py-2 cursor-pointer user-select-none" 
        style={{ backgroundColor: "#1e293b" }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="d-flex align-items-center">
          <i className={`bi ${icon} text-white me-2`} style={{ fontSize: '0.9rem' }}></i>
          <h6 className="fw-bold m-0 text-white text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
            {title}
          </h6>
        </div>
        <i className={`bi ${isOpen ? 'bi-chevron-up' : 'bi-chevron-down'} text-white`} style={{ fontSize: '0.8rem' }}></i>
      </div>
      {isOpen && (
        <div className="p-3 border-top bg-light bg-opacity-10">
          {children}
        </div>
      )}
    </div>
  );
};

/* ===================== MAIN PROFILE PAGE ===================== */
const EmployeeProfile = () => {
  const detailsRef = useRef();
  const personalRef = useRef();
  const addressRef = useRef();
  const emergencyRef = useRef();
  
  const navigate = useNavigate();
  const { id } = useParams();

  const [isSaving, setIsSaving] = useState(false);
  const [saveResults, setSaveResults] = useState(null);

  const handleGlobalSave = async () => {
    setIsSaving(true);
    setSaveResults(null);

    const sections = [
      { name: "Employment Details", ref: detailsRef },
      { name: "Personal Details", ref: personalRef },
      { name: "Address Information", ref: addressRef },
      { name: "Emergency Contacts", ref: emergencyRef },
    ];

    const savePromises = sections.map(async (section) => {
      if (section.ref.current?.submit) {
        try {
          await section.ref.current.submit();
          return { name: section.name, status: "success" };
        } catch (error) {
          return { name: section.name, status: "error" };
        }
      }
      return null;
    });

    const results = await Promise.all(savePromises);
    setSaveResults(results.filter(r => r !== null));
    setIsSaving(false);

    // Keep it in the middle for 5 seconds or until closed
    setTimeout(() => setSaveResults(null), 5000);
  };

  return (
    <Layout>
{/* 1. GLOBAL STATUS NOTIFICATION (FORCED CENTER) */}
      {saveResults && (
        <div 
          className="position-fixed top-0 start-0 w-100 vh-100 d-flex align-items-center justify-content-center" 
          style={{ 
            zIndex: 10000, 
            backgroundColor: 'rgba(0, 0, 0, 0.45)', // Slightly darker backdrop
            backdropFilter: 'blur(3px)'           // Professional blur effect
          }}
          onClick={() => setSaveResults(null)}
        >
          {/* Centered Summary Card */}
          <div 
            className="bg-white shadow-lg rounded-3 border-0 overflow-hidden" 
            style={{ 
              width: '420px', 
              maxWidth: '90vw',
              animation: 'fadeIn 0.2s ease-out' 
            }}
            onClick={(e) => e.stopPropagation()} // Prevents closing when clicking inside the card
          >
            {/* Modal Header */}
            <div className="bg-dark text-white px-4 py-3 d-flex justify-content-between align-items-center">
              <span className="fw-bold" style={{ fontSize: '0.85rem', letterSpacing: '0.05em' }}>
                <i className="bi bi-info-circle me-2 text-info"></i>SAVE SUMMARY
              </span>
              <i className="bi bi-x-lg cursor-pointer" style={{ fontSize: '0.9rem' }} onClick={() => setSaveResults(null)}></i>
            </div>
            
            {/* Modal Body */}
            <div className="p-4">
              {saveResults.map((res, index) => (
                <div key={index} className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom border-light">
                  <span className="text-secondary fw-semibold" style={{ fontSize: '0.9rem' }}>{res.name}</span>
                  <div className="d-flex align-items-center">
                    <span className={`me-2 fw-bold text-uppercase`} style={{ fontSize: '0.7rem' }}>
                      {res.status === 'success' ? (
                        <span className="text-success">Success</span>
                      ) : (
                        <span className="text-danger">Failed</span>
                      )}
                    </span>
                    <i className={`bi ${res.status === 'success' ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'} fs-5`}></i>
                  </div>
                </div>
              ))}
              
              <button 
                className="btn btn-dark w-100 mt-2 fw-bold py-2 shadow-sm" 
                style={{ borderRadius: '8px' }}
                onClick={() => setSaveResults(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. Header Bar */}
      <div className="d-flex justify-content-between align-items-center mb-4 mt-2 px-2">
        <h2 className="fw-bold text-dark m-0">
          {!id ? "Create Employee" : "Employee Profile"}
        </h2>
        
        <div className="d-flex gap-2">
          <button 
            className="btn text-white btn-sm px-3 fw-bold shadow-sm" 
            style={{ height: '35px', backgroundColor: '#0f172a', border: 'none', display: 'flex', alignItems: 'center' }}
            onClick={() => navigate(-1)}
          >
            <i className="bi bi-arrow-left-circle me-2"></i> Back
          </button>
          
          {id && (
            <button 
              className={`btn text-white btn-sm px-3 fw-bold shadow-sm ${isSaving ? 'disabled' : ''}`}
              style={{ height: '35px', backgroundColor: '#198754', border: 'none', display: 'flex', alignItems: 'center' }}
              onClick={handleGlobalSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Saving Profile...
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-2"></i> Save Profile
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* 3. Main Content Areas */}
      <div className="mb-4">
        <EmployeeDetails ref={detailsRef}/>
      </div>

      {id && (
        <div className="pb-5">
          <CollapsibleSection title="Personal Details" icon="bi-person-vcard" defaultOpen={false}>
            <EmployeePersonalDetails ref={personalRef}/>
          </CollapsibleSection>

          <CollapsibleSection title="Address Information" icon="bi-geo-alt">
            <EmployeeAddress ref={addressRef}/>
          </CollapsibleSection>

          <CollapsibleSection title="Employee Contacts" icon="bi-telephone-outbound">
            <EmployeeEmergencyContact ref={emergencyRef}/>
          </CollapsibleSection>

          <CollapsibleSection title="Employee Files" icon="bi-shield-check">
            <GovernmentDocs />
          </CollapsibleSection>

          <CollapsibleSection title="Employment History" icon="bi-briefcase">
            <EmployeeHistory />
          </CollapsibleSection>

          <CollapsibleSection title="Educational Background" icon="bi-mortarboard">
            <EmployeeEducationBackground />
          </CollapsibleSection>
        </div>
      )}
    </Layout>
  );
};


export default EmployeeProfile;